<template>
  <div class="musubi-page musubi-page--replies">
    <header class="musubi-page__head">
      <h1>Reply inbox <span class="plan-pill">Growth+</span></h1>
      <p class="text-muted">
        Every reply to your campaigns and drips, read once by AI and sorted so the money lands on top.
        <b>Interested</b> replies are paused and flagged for you here; out-of-office auto-responders resume the
        sequence when the person is back; unsubscribe requests are honoured on the spot. You review — the inbox
        never sends anything on its own.
      </p>
    </header>

    <!-- ── Gated teaser (non-Growth): violet plan-gate. Blurred skeleton rows sit
         behind the upgrade panel. `gateLocked` is null while /me/usage loads. ── -->
    <div v-if="gateLocked" class="rp-teaser">
      <div class="rp-teaser__blur" aria-hidden="true">
        <div class="rp-skel-tbl">
          <div v-for="n in 4" :key="n" class="rp-skel-row">
            <span class="skel" style="width:26%"></span>
            <span class="skel" style="width:14%"></span>
            <span class="skel" style="width:28%"></span>
            <span class="skel" style="width:10%"></span>
          </div>
        </div>
      </div>
      <div class="rp-teaser__panel">
        <div class="upgrade">
          <div class="upgrade__ic"><CygnusIcon name="bolt" /></div>
          <div class="upgrade__eyebrow">Growth &amp; up · reply_triage</div>
          <h3>Let AI sort your replies for you</h3>
          <p>
            On Starter, replies still arrive and your reply timeline works exactly as it does today. What's
            gated is the <b>triage</b> — the AI classification, the safe auto-actions, and this sorted inbox.
          </p>
          <div class="upgrade__benefits">
            <div class="benefit"><CygnusIcon name="check" /> <span><b>Interested replies float to the top</b>, paused and flagged the moment they land.</span></div>
            <div class="benefit"><CygnusIcon name="check" /> <span><b>Out-of-office resumes itself</b> on the date they say they're back.</span></div>
            <div class="benefit"><CygnusIcon name="check" /> <span><b>Unsubscribes honoured instantly</b>, through the same opt-out path as the link.</span></div>
          </div>
          <div class="upgrade__honest">
            <CygnusIcon name="info-circle" />
            <span>To be straight with you: you're not missing any replies today — they all reach your timeline.
              Growth adds the reading and the sorting on top.</span>
          </div>
          <div class="upgrade__actions">
            <router-link to="/admin/usage-plans" class="btn rp-btn-violet"><CygnusIcon name="bolt" /> Upgrade to Growth</router-link>
            <router-link to="/admin/usage-plans" class="rp-compare">Compare plans <CygnusIcon name="arrow-right" /></router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Entitled page ── -->
    <template v-else-if="gateLocked === false">
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <div class="coverage-note">
        <CygnusIcon name="info-circle" />
        <span>
          The AI classifies each reply and, above a <b>0.80 confidence</b> threshold, takes the safe automatic
          step (pause / resume / unsubscribe). Anything below that lands in <b>Needs review</b> untouched, for
          you to confirm. We store the classification and the facts it lifted — <b>never the reply's actual text</b>.
        </span>
      </div>

      <section class="rp-kpis">
        <div class="m-kpi">
          <div class="m-kpi__label"><CygnusIcon name="note-sticky" class="m-kpi__icon" /><span>Replies</span></div>
          <div class="m-kpi__value">{{ counts.all || 0 }}</div>
          <div class="m-kpi__delta">across your campaigns &amp; drips</div>
        </div>
        <div class="m-kpi money">
          <div class="m-kpi__label"><CygnusIcon name="check" class="m-kpi__icon success" /><span>Interested</span></div>
          <div class="m-kpi__value success">{{ counts.interested || 0 }}</div>
          <div class="m-kpi__delta up">the money bucket</div>
        </div>
        <div class="m-kpi review">
          <div class="m-kpi__label"><CygnusIcon name="warning" class="m-kpi__icon warning" /><span>Needs review</span></div>
          <div class="m-kpi__value warning">{{ counts.needs_review || 0 }}</div>
          <div class="m-kpi__delta warning">below the confidence gate</div>
        </div>
        <div class="m-kpi">
          <div class="m-kpi__label"><CygnusIcon name="bolt" class="m-kpi__icon teal" /><span>Auto-handled</span></div>
          <div class="m-kpi__value">{{ autoHandled }}</div>
          <div class="m-kpi__delta teal">actioned without you lifting a finger</div>
        </div>
      </section>

      <!-- classification tabs -->
      <div class="rp-tabs">
        <button
          v-for="t in tabs" :key="t.key"
          class="rp-tab" :class="{ active: tab === t.key, moneytab: t.money }"
          @click="setTab(t.key)"
        >
          <span class="tdot" :class="t.dot"></span>{{ t.label }}
          <span v-if="counts[t.countKey]" class="count">{{ counts[t.countKey] }}</span>
        </button>
      </div>

      <div class="rp-toolbar">
        <select v-model="filters.status" class="rp-select" @change="load">
          <option value="">Any status</option>
          <option value="new">New</option>
          <option value="handled">Handled</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <!-- Campaign / flow source filter (server-side). Only shown once the
             tenant has replies from at least one campaign or flow. -->
        <select v-if="hasSources" v-model="filters.source" class="rp-select" @change="load" title="Filter by the campaign or drip flow the reply answered">
          <option value="">All campaigns &amp; flows</option>
          <optgroup v-if="sources.campaigns.length" label="Campaigns">
            <option v-for="c in sources.campaigns" :key="'c' + c.id" :value="'c:' + c.id">{{ c.name }}</option>
          </optgroup>
          <optgroup v-if="sources.flows.length" label="Drip flows">
            <option v-for="f in sources.flows" :key="'f' + f.id" :value="'f:' + f.id">{{ f.name }}</option>
          </optgroup>
        </select>
        <input v-model="filters.search" class="rp-input" placeholder="Search name or email…" style="width:210px" @keyup.enter="load" @blur="load">
      </div>

      <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

      <div v-if="loading" class="rp-loading">Loading replies…</div>

      <!-- Empty state -->
      <div v-else-if="!rows.length" class="dc-empty teal-edge">
        <div class="dc-empty__icon"><CygnusIcon name="note-sticky" /></div>
        <div class="dc-empty__title">
          {{ counts.all ? 'No replies match these filters' : 'No replies to triage yet' }}
        </div>
        <div class="dc-empty__hint">
          Reply triage is live and watching. As soon as someone answers one of your campaigns or drips, the AI
          will read it, sort it, and — when it's confident — take the safe step for you.
        </div>
        <div class="dc-empty__why">
          <div class="row"><CygnusIcon name="info-circle" /><span><b>Replies track your sending.</b> An empty inbox usually just means nothing has gone out yet, or your live campaigns haven't landed a reply. It fills as your sequences run.</span></div>
          <div class="row"><CygnusIcon name="bell" /><span><b>You'll hear about it.</b> The first interested reply raises one bell notification — you don't have to watch this page.</span></div>
        </div>
        <div class="dc-empty__actions">
          <router-link to="/outreach/campaigns" class="btn rp-btn-primary"><CygnusIcon name="envelope" /> View campaigns</router-link>
          <router-link to="/outreach/drip-flows" class="btn rp-btn-secondary"><CygnusIcon name="diagram" /> View drip flows</router-link>
        </div>
        <div class="foot-note">Nothing here is broken — reply volume simply follows sending volume.</div>
      </div>

      <!-- Table -->
      <div v-else class="rp-tbl">
        <table>
          <thead>
            <tr>
              <th style="width:30px"></th>
              <th>Who replied</th>
              <th>Classification</th>
              <th>What we did</th>
              <th>From</th>
              <th>When</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in rows" :key="r.id">
              <tr :class="rowClass(r)">
                <td><button class="chev" :class="{ open: expanded === r.id }" @click="toggleExpand(r.id)"><CygnusIcon name="chevron-right" /></button></td>
                <td>
                  <div class="sub-name">{{ r.subscriber_name || 'Unknown sender' }}</div>
                  <div class="sub-email"><CygnusIcon name="envelope" />{{ r.subscriber_email }}</div>
                  <div v-if="r.company_name" class="sub-co"><CygnusIcon name="building" />{{ r.company_name }}</div>
                </td>
                <td>
                  <span class="cls-badge" :class="[badgeClass(r), { outlined: needsReview(r) }]">
                    <CygnusIcon name="bolt" class="spark" /><span class="dot"></span>{{ badgeLabel(r) }}
                  </span>
                  <div class="conf">
                    <div class="conf__bar">
                      <span class="conf__track"><span :class="needsReview(r) ? 'warning' : 'success'" :style="{ width: confPct(r) }"></span></span>
                      <span class="conf__num">{{ r.confidence.toFixed(2) }}</span>
                    </div>
                    <span class="conf__state" :class="needsReview(r) ? 'needs' : 'auto'">
                      <CygnusIcon :name="needsReview(r) ? 'warning' : 'check'" />
                      {{ needsReview(r) ? 'Needs review' : 'Auto-actioned' }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="did" :class="didTone(r)">
                    <CygnusIcon :name="didIcon(r)" />
                    <span><span class="em">{{ didHead(r) }}</span> {{ didTail(r) }}</span>
                  </div>
                </td>
                <td>
                  <div v-if="sourceLabel(r)" class="src"><CygnusIcon :name="r.source_flow ? 'diagram' : 'envelope'" />{{ sourceLabel(r) }}</div>
                  <div v-else class="src text-muted">—</div>
                  <div v-if="r.source_subject" class="src__step"><span class="pill">Re</span>{{ r.source_subject }}</div>
                </td>
                <td><span class="age" :class="{ fresh: isFresh(r) }"><CygnusIcon :name="isFresh(r) ? 'bolt' : 'clock'" />{{ ageLabel(r.created) }}</span></td>
                <td>
                  <div class="row-actions">
                    <button v-if="r.prospect_id" class="btn rp-btn-secondary btn-sm" @click="openConversation(r)"><CygnusIcon name="note-sticky" /> Open conversation</button>
                    <template v-if="r.status === 'handled'">
                      <span class="rp-stamp"><CygnusIcon name="check" /> Handled</span>
                    </template>
                    <button v-else-if="r.classification === 'interested'" class="btn rp-btn-primary btn-sm" :disabled="busy" @click="markHandled(r)"><CygnusIcon name="check" /> Mark handled</button>
                    <button class="btn-ghost" :disabled="busy" title="More" @click="toggleMenu(r.id)"><CygnusIcon name="more" /></button>
                    <div v-if="menuOpen === r.id" class="rp-menu">
                      <button @click="startReclassify(r)"><CygnusIcon name="tags" /> Reclassify</button>
                      <button v-if="r.status !== 'handled'" @click="markHandled(r)"><CygnusIcon name="check" /> Mark handled</button>
                      <button v-if="r.status !== 'dismissed'" @click="dismiss(r)"><CygnusIcon name="x" /> Dismiss</button>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Drill-down -->
              <tr v-if="expanded === r.id" class="drill">
                <td colspan="7" style="padding:0">
                  <div class="drill__inner">
                    <div>
                      <div class="ai-read">
                        <div class="ai-read__head"><CygnusIcon name="bolt" />How the AI read this<span class="ai-read__badge">reply.triage</span></div>
                        <div class="ai-read__body">
                          Classified as <b>{{ badgeLabel(r).toLowerCase() }}</b> — <b>{{ r.confidence.toFixed(2) }} confidence</b>.
                          {{ readoutTail(r) }}
                        </div>
                        <div class="ai-read__note">
                          <CygnusIcon name="info-circle" />
                          <span>The reply's actual wording isn't stored — only the classification, confidence and any facts
                            lifted below. Open the conversation to read the real thread.</span>
                        </div>
                      </div>

                      <h4><CygnusIcon name="list" /> What the AI lifted</h4>
                      <div class="facts">
                        <div v-for="(f, i) in factChips(r)" :key="i" class="fact-chip" :class="f.cls"><span class="k">{{ f.k }}</span>{{ f.v }}</div>
                      </div>

                      <h4><CygnusIcon name="clock" /> Action history</h4>
                      <div class="timeline2">
                        <div v-if="r.action_taken && r.action_taken !== 'none'" class="tl2 auto">
                          <div class="tl2__t">{{ didHead(r) }} <span class="mini">Auto</span></div>
                          <div class="tl2__m">{{ fmtDateTime(r.modified) }} · action_taken: {{ r.action_taken }}</div>
                        </div>
                        <div class="tl2 ai">
                          <div class="tl2__t">Classified {{ r.classification }} · {{ r.confidence.toFixed(2) }} <span class="mini ai">AI</span></div>
                          <div class="tl2__m">{{ fmtDateTime(r.created) }} · reply.triage</div>
                        </div>
                        <div class="tl2">
                          <div class="tl2__t">Reply received</div>
                          <div class="tl2__m">{{ fmtDateTime(r.created) }}<span v-if="sourceLabel(r)"> · {{ sourceLabel(r) }}</span></div>
                        </div>
                        <div v-for="(log, i) in reclassifyLog(r)" :key="'rl' + i" class="tl2">
                          <div class="tl2__t">Reclassified {{ log.from }} → {{ log.to }} <span class="mini log">Manual</span></div>
                          <div class="tl2__m">{{ fmtDateTime(log.at) }} · human override (reverses nothing)</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <!-- Wrong-person suggested contact (dual provenance) -->
                      <template v-if="r.classification === 'wrong_person' && suggestion(r)">
                        <h4><CygnusIcon name="user-check" /> Who to contact instead</h4>
                        <div class="suggest">
                          <div class="suggest__head"><CygnusIcon name="arrow-right" /> Suggested contact</div>
                          <div class="suggest__body">
                            <div class="contact-card">
                              <span class="contact-card__av">{{ initials(suggestion(r).name) }}</span>
                              <div>
                                <div class="contact-card__name">{{ suggestion(r).name || 'Named in the reply' }}</div>
                                <div class="contact-card__email">{{ suggestion(r).email || 'Named in the reply — email not given' }}</div>
                                <span class="prov" :class="suggestion(r).source === 'enricher_verified' ? 'verified' : 'referral'">
                                  <CygnusIcon :name="suggestion(r).source === 'enricher_verified' ? 'shield' : 'arrow-left'" />
                                  {{ suggestion(r).source === 'enricher_verified' ? 'Enricher-verified · named contact' : 'From the reply' }}
                                </span>
                              </div>
                            </div>
                            <div class="suggest__note">
                              <CygnusIcon name="info-circle" />
                              <span>A suggestion is not an action — nothing has been sent to this person. You choose whether to reach out.</span>
                            </div>
                            <div class="suggest__cta">
                              <button class="btn rp-btn-violet" :disabled="busy || !suggestion(r).email" @click="startNewConversation(r)"><CygnusIcon name="note-sticky" /> Start new conversation</button>
                              <button class="btn rp-btn-secondary" :disabled="busy || !suggestion(r).email" @click="addAsContact(r)"><CygnusIcon name="plus" /> Add as contact</button>
                            </div>
                          </div>
                        </div>
                      </template>

                      <h4><CygnusIcon name="user" /> Linked prospect</h4>
                      <div v-if="r.prospect_id" class="prospect">
                        <div class="prospect__top">
                          <span class="prospect__name">{{ r.subscriber_name }}<span v-if="r.company_name"> · {{ r.company_name }}</span></span>
                          <router-link :to="'/acquire/prospects/' + r.prospect_id" class="prospect__link">Open prospect <CygnusIcon name="arrow-right" /></router-link>
                        </div>
                        <div class="prospect__meta">
                          <span class="stage-chip"><CygnusIcon name="diagram" />Stage: {{ pretty(r.prospect_stage) || '—' }}</span>
                          <span class="temp" :class="tempClass(r.prospect_temperature)"><span class="dot"></span>{{ prettyTemp(r.prospect_temperature) }}</span>
                        </div>
                      </div>
                      <div v-else class="text-muted small">No prospect is linked to this reply.</div>

                      <!-- Reclassify inline popover -->
                      <div v-if="reclassifyFor === r.id" class="reclass-pop">
                        <div class="reclass-pop__head">
                          <div class="t">Reclassify this reply</div>
                          <div class="s">Currently <b>{{ badgeLabel(r) }}</b> · {{ r.confidence.toFixed(2) }} confidence · {{ r.subscriber_name }}</div>
                        </div>
                        <div class="reclass-list">
                          <div
                            v-for="opt in reclassOptions" :key="opt.key"
                            class="reclass-opt" :class="{ current: opt.key === r.classification }"
                            @click="opt.key !== r.classification && applyReclassify(r, opt.key)"
                          >
                            <span class="odot" :style="{ background: opt.color }"></span>
                            <span class="oname">{{ opt.label }}</span>
                            <span v-if="opt.key === r.classification" class="ocur">Current</span>
                            <CygnusIcon name="check" class="ocheck" v-else />
                          </div>
                        </div>
                        <div class="reclass-pop__foot">
                          <div class="reclass-warn">
                            <CygnusIcon name="warning" />
                            <span>Reclassifying is recorded, but it <b>reverses nothing</b> — if the AI already unsubscribed
                              someone, that stands. Undo it yourself if it was wrong.</span>
                          </div>
                          <div class="reclass-pop__actions"><button class="btn btn-ghost btn-sm" @click="reclassifyFor = null">Cancel</button></div>
                        </div>
                      </div>

                      <div class="drill__cta">
                        <button v-if="r.prospect_id" class="btn rp-btn-secondary" @click="openConversation(r)"><CygnusIcon name="note-sticky" /> Open conversation</button>
                        <button v-if="r.status !== 'handled'" class="btn rp-btn-primary" :disabled="busy" @click="markHandled(r)"><CygnusIcon name="check" /> Mark handled</button>
                        <button class="btn btn-ghost btn-sm" @click="startReclassify(r)"><CygnusIcon name="tags" /> Reclassify</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Per-contact compose (follow-up-pool slice A) — wrong-person "Start new
         conversation" opens this to the referred/verified contact. -->
    <MusubiComposeModal :open="composeOpen" :context="composeContext" @close="composeOpen = false" @sent="onComposed" />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * AdminReplies — the tenant-facing AI reply-triage inbox (Growth Feature 4 UI).
 * Mirrors AdminTechAlerts / AdminCompaniesHouse: a plan-gated list with a
 * classification-tabbed lifecycle, an expandable drill-down, and commit actions.
 *
 * The honesty carried by the mockup is load-bearing and preserved verbatim:
 *   - the reply BODY is never stored — the AI read-out renders only what the
 *     stored fields (classification / confidence / extracted facts / action_taken)
 *     can actually support, never an invented explanation;
 *   - "Needs review" is a synthetic tab (below-the-gate, still-new rows), counted
 *     server-side, distinct from the six real classification buckets;
 *   - reclassify appends to extracted_json.reclassify_log and reverses nothing;
 *   - the wrong-person card shows dual-provenance contacts and NEVER auto-emails
 *     the referred person — "Add as contact" only creates a subscriber, and
 *     "Start new conversation" is a manual, violet decision.
 *
 * "Open conversation" deep-links the prospect page (/acquire/prospects/{id}),
 * where the real Engagement timeline (the reply thread) lives. A non-Growth
 * tenant sees the violet teaser (the server 403s the reads anyway).
 */
export default {
  name: "AdminReplies",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      gateLocked: null, // null=loading, true=teaser, false=entitled
      loading: false,
      busy: false,
      error: null,
      message: "",
      tab: "interested", // default = the money tab
      tabs: [
        { key: "interested", label: "Interested", countKey: "interested", dot: "success", money: true },
        { key: "needs_review", label: "Needs review", countKey: "needs_review", dot: "warning" },
        { key: "wrong_person", label: "Wrong person", countKey: "wrong_person", dot: "neutral" },
        { key: "out_of_office", label: "Out of office", countKey: "out_of_office", dot: "neutral" },
        { key: "unsubscribe_request", label: "Unsubscribed", countKey: "unsubscribe_request", dot: "quiet" },
        { key: "", label: "All", countKey: "all", dot: "all" },
      ],
      filters: { status: "", search: "", source: "" },
      sources: { campaigns: [], flows: [] },
      composeOpen: false,
      composeContext: {},
      counts: { all: 0, interested: 0, needs_review: 0, wrong_person: 0, out_of_office: 0, unsubscribe_request: 0, not_now: 0, other: 0 },
      rows: [],
      total: 0,
      expanded: null,
      menuOpen: null,
      reclassifyFor: null,
      gate: 0.8,
      reclassOptions: [
        { key: "interested", label: "Interested", color: "#0D9488" },
        { key: "not_now", label: "Not now", color: "#D97706" },
        { key: "wrong_person", label: "Wrong person", color: "#5B6B85" },
        { key: "out_of_office", label: "Out of office", color: "#5B6B85" },
        { key: "unsubscribe_request", label: "Unsubscribe request", color: "#AEB6C4" },
        { key: "other", label: "Other", color: "#AEB6C4" },
      ],
      badgeMap: {
        interested: { cls: "interested", label: "Interested" },
        not_now: { cls: "notnow", label: "Not now?" },
        wrong_person: { cls: "wrong", label: "Wrong person" },
        out_of_office: { cls: "ooo", label: "Out of office" },
        unsubscribe_request: { cls: "unsub", label: "Unsubscribe" },
        other: { cls: "other", label: "Other" },
      },
    };
  },
  computed: {
    // Everything not needing a human read was resolved by the system.
    autoHandled() {
      return Math.max(0, (this.counts.all || 0) - (this.counts.needs_review || 0));
    },
    hasSources() {
      return (this.sources.campaigns || []).length > 0 || (this.sources.flows || []).length > 0;
    },
  },
  created() {
    this.checkGate();
  },
  methods: {
    async checkGate() {
      try {
        const res = await this.api.get("/api/v1/me/usage");
        const body = res?.data ?? res;
        const entitled = !!body?.features?.reply_triage || !!body?.plan?.can_manage;
        this.gateLocked = !entitled;
      } catch (e) {
        this.gateLocked = false; // fail-open; server gate stays authoritative
      }
      if (this.gateLocked === false) this.load();
    },
    async load() {
      this.loading = true;
      this.error = null;
      this.expanded = null;
      this.menuOpen = null;
      this.reclassifyFor = null;
      try {
        const qs = new URLSearchParams({
          classification: this.tab,
          status: this.filters.status,
          search: this.filters.search,
          source: this.filters.source,
          per_page: "100",
        });
        const res = await this.api.get("/api/v1/admin/reply-triage?" + qs.toString());
        const body = res?.data ?? res;
        this.rows = Array.isArray(body?.rows) ? body.rows : [];
        this.counts = { ...this.counts, ...(body?.counts || {}) };
        // Distinct campaign/flow sources (independent of the current filter) for
        // the toolbar dropdown. Kept across loads so the selected option survives.
        if (body?.sources) this.sources = { campaigns: body.sources.campaigns || [], flows: body.sources.flows || [] };
        this.total = Number(body?.total) || this.rows.length;
      } catch (e) {
        this.error = this.errText(e, "Could not load your replies.");
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    setTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.load();
    },
    toggleExpand(id) {
      this.expanded = this.expanded === id ? null : id;
      this.menuOpen = null;
      if (this.expanded !== id) this.reclassifyFor = null;
    },
    toggleMenu(id) {
      this.menuOpen = this.menuOpen === id ? null : id;
    },
    // ── classification / confidence treatment ──
    badgeClass(r) { return (this.badgeMap[r.classification] || { cls: "other" }).cls; },
    badgeLabel(r) { return (this.badgeMap[r.classification] || { label: r.classification }).label; },
    // Below-the-gate / not-auto-actioned + still new = needs a human read.
    needsReview(r) {
      const acted = r.action_taken && !["none", "", "quota_exhausted", "classify_unavailable"].includes(r.action_taken);
      return r.status === "new" && (!acted || r.confidence < this.gate);
    },
    confPct(r) { return Math.round((Number(r.confidence) || 0) * 100) + "%"; },
    rowClass(r) {
      return {
        "is-open": this.expanded === r.id,
        "money-row": r.classification === "interested",
        "review-row": this.needsReview(r),
      };
    },
    // ── what-we-did (action_taken → plain English) ──
    didTone(r) {
      return {
        auto: r.action_taken === "drip_paused" || r.action_taken === "suggestion_stored",
        delay: r.action_taken === "ooo_delayed",
        stop: r.action_taken === "unsubscribed",
        hold: this.needsReview(r) || !r.action_taken || r.action_taken === "none",
      };
    },
    didIcon(r) {
      switch (r.action_taken) {
        case "drip_paused": return "pause";
        case "ooo_delayed": return "clock";
        case "unsubscribed": return "x-circle";
        case "suggestion_stored": return "user-check";
        default: return "warning";
      }
    },
    didHead(r) {
      switch (r.action_taken) {
        case "drip_paused": return "Paused the sequence";
        case "ooo_delayed": return "Automation resumes " + (this.resumeDate(r) || "later");
        case "unsubscribed": return "Unsubscribed them immediately";
        case "suggestion_stored": return "Paused and found who to contact instead";
        case "quota_exhausted": return "Not classified";
        case "classify_unavailable": return "Couldn't classify automatically";
        case "reclassified": return "You reclassified this manually";
        default: return "Nothing automatic";
      }
    },
    didTail(r) {
      switch (r.action_taken) {
        case "drip_paused": return "and flagged for you.";
        case "ooo_delayed": return "— the day after they said they're back.";
        case "unsubscribed": return "— honoured through the same opt-out path as the link.";
        case "suggestion_stored": return "— a suggestion only, nothing sent.";
        case "quota_exhausted": return "— your AI allowance was spent when it landed. Waiting for your read.";
        case "classify_unavailable": return "— the AI engine was unavailable. Waiting for your read.";
        case "reclassified": return "— the automation was left exactly as it was.";
        default: return "— below the 0.80 gate, so it's waiting for your read.";
      }
    },
    readoutTail(r) {
      switch (r.action_taken) {
        case "drip_paused": return "The sequence was paused and flagged for you — the drip had already exited on reply, this is the idempotent belt.";
        case "ooo_delayed": return "Read as an out-of-office auto-responder; the sequence was rescheduled to resume " + (this.resumeDate(r) || "after they return") + ".";
        case "unsubscribed": return "Read as an opt-out request and honoured immediately through the same path as the unsubscribe link.";
        case "suggestion_stored": return "The reply pointed at someone else; the drip was paused and a contact suggestion stored — no message was sent to anyone.";
        case "quota_exhausted": return "This reply wasn't classified — the workspace's AI allowance was spent when it landed. It's here for you to read.";
        case "classify_unavailable": return "The AI engine was unavailable when this landed, so it wasn't classified automatically. It's here for you to read.";
        default: return "This sits below the 0.80 auto-action gate, so nothing automatic happened — it's waiting for your read.";
      }
    },
    // ── source (From column) ──
    sourceLabel(r) { return r.source_campaign || r.source_flow || null; },
    // ── drill-down facts ──
    factChips(r) {
      const ex = r.extracted || {};
      const chips = [];
      if (r.action_taken === "ooo_delayed" && this.resumeDate(r)) chips.push({ k: "Resumes", v: this.resumeDate(r), cls: "date" });
      const s = this.suggestion(r);
      if (s) {
        if (s.name) chips.push({ k: "Referred", v: s.name, cls: "" });
        if (s.email) chips.push({ k: "Contact", v: s.email, cls: "" });
        chips.push({ k: "Source", v: s.source === "enricher_verified" ? "Enricher-verified" : "From the reply", cls: "" });
      }
      if (r.classification === "interested") chips.push({ k: "Signal", v: "Buying intent", cls: "" });
      chips.push({ k: "Reply body", v: "Not stored", cls: "" });
      return chips;
    },
    suggestion(r) {
      const s = (r.extracted || {}).suggested_contact;
      return s && (s.name || s.email) ? s : null;
    },
    reclassifyLog(r) {
      const log = (r.extracted || {}).reclassify_log;
      return Array.isArray(log) ? log : [];
    },
    resumeDate(r) {
      const at = (r.extracted || {}).resume_at || (r.extracted || {}).ooo_return_date;
      return at ? this.fmtDate(at) : null;
    },
    // ── actions ──
    openConversation(r) {
      if (r.prospect_id) this.$router.push("/acquire/prospects/" + r.prospect_id + "?from=reply-triage");
    },
    async markHandled(r) {
      await this.transition(r, "handled", "Marked handled.");
    },
    async dismiss(r) {
      await this.transition(r, "dismissed", "Reply dismissed.");
    },
    async transition(r, status, okMsg) {
      this.busy = true;
      this.error = null;
      this.menuOpen = null;
      try {
        await this.api.post("/api/v1/admin/reply-triage/" + r.id + "/" + status, {});
        this.message = okMsg;
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Could not update this reply.");
      } finally {
        this.busy = false;
      }
    },
    startReclassify(r) {
      this.expanded = r.id;
      this.menuOpen = null;
      this.reclassifyFor = r.id;
    },
    async applyReclassify(r, classification) {
      this.busy = true;
      this.error = null;
      try {
        await this.api.post("/api/v1/admin/reply-triage/" + r.id + "/reclassify", { classification });
        this.message = "Reclassification recorded — it reverses nothing.";
        this.reclassifyFor = null;
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Could not reclassify.");
      } finally {
        this.busy = false;
      }
    },
    // Wrong-person: create the referred person as a subscriber. NEVER emails them.
    async addAsContact(r) {
      const s = this.suggestion(r);
      if (!s || !s.email) return;
      this.busy = true;
      this.error = null;
      try {
        await this.api.post("/api/v1/admin/subscribers", {
          email: s.email,
          name: s.name || null,
          basis: "legitimate_interest",
          source: "reply_referral",
        });
        this.message = "Added " + (s.name || s.email) + " as a contact. Nothing was emailed to them.";
      } catch (e) {
        // Already-exists is a benign 422 — treat as success for the flow.
        const body = e?.response?.data ?? e?.data;
        if (body?.error && String(body.error).includes("already exists")) {
          this.message = (s.name || s.email) + " is already a contact.";
        } else {
          this.error = this.errText(e, "Could not add the contact.");
        }
      } finally {
        this.busy = false;
      }
    },
    // "Start new conversation" — a deliberate manual decision. Opens the
    // per-contact compose panel prefilled with the referred/verified contact.
    // Sending goes through the standard machinery (metered, tracked, opt-out
    // gated); the contact is created on send. Nothing is auto-emailed — the
    // person is only mailed if the user hits Queue.
    startNewConversation(r) {
      const s = this.suggestion(r);
      if (!s || !s.email) return;
      this.composeContext = {
        to_email: s.email,
        to_name: s.name || "",
        subject: "",
        // No prospect_id — the referred person is NOT the mis-contacted prospect.
      };
      this.composeOpen = true;
    },
    onComposed() {
      this.message = "Queued — the email will send on the next queue tick.";
    },
    // ── prospect card ──
    tempClass(t) { return t === "warm" ? "warm" : (t === "hot" ? "hot" : "cold"); },
    prettyTemp(t) { return t ? this.pretty(t) : "Cold"; },
    initials(name) {
      const n = String(name || "").trim();
      if (!n) return "?";
      return n.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
    },
    // ── formatting ──
    pretty(s) {
      if (s === null || s === undefined || s === "") return "";
      return String(s).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    isFresh(r) {
      if (!r.created) return false;
      return (Date.now() / 1000 - Number(r.created)) / 86400 <= 1;
    },
    ageLabel(epoch) {
      epoch = Number(epoch);
      if (!epoch) return "—";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - epoch);
      if (s < 3600) return Math.max(1, Math.floor(s / 60)) + "m ago";
      if (s < 86400) return Math.floor(s / 3600) + "h ago";
      const days = Math.floor(s / 86400);
      if (days === 1) return "Yesterday";
      if (days < 30) return days + " days ago";
      return this.fmtDate(epoch);
    },
    fmtDate(epoch) {
      if (!epoch) return "—";
      return new Date(Number(epoch) * 1000).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    },
    fmtDateTime(epoch) {
      if (!epoch) return "—";
      const d = new Date(Number(epoch) * 1000);
      return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) + ", " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === "plan_gated") return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.musubi-page--replies { max-width: 1240px; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 780px; line-height: 1.55; font-size: 0.92rem; }
.text-muted { color: var(--m-text-secondary); }
.plan-pill { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--m-accent-hover, #4338CA); background: var(--m-accent-soft, #EEF0FF); border: 1px solid #D9DCF7; border-radius: 999px; padding: 3px 9px; position: relative; top: -2px; }

.coverage-note { display: flex; align-items: flex-start; gap: 9px; margin: 16px 0 8px; font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; }
.coverage-note i { color: var(--m-status-info); margin-top: 2px; }

/* KPIs */
.rp-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 8px 0; }
@media (max-width: 1024px) { .rp-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .rp-kpis { grid-template-columns: 1fr; } }
.m-kpi { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); padding: var(--m-space-3, 12px) var(--m-space-4, 16px); box-shadow: var(--m-shadow-card); }
.m-kpi.money { border-color: #BBE7DF; background: linear-gradient(180deg, #F3FBF9, var(--m-bg-card)); }
.m-kpi.review { border-color: #F0DCBB; background: linear-gradient(180deg, #FFFAF2, var(--m-bg-card)); }
.m-kpi__label { display: flex; align-items: center; gap: 7px; color: var(--m-text-secondary); font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.m-kpi__icon { color: var(--m-accent); }
.m-kpi__icon.success { color: var(--m-status-success, #0D9488); }
.m-kpi__icon.warning { color: var(--m-status-warning, #D97706); }
.m-kpi__icon.teal { color: var(--m-teal, #14B8A6); }
.m-kpi__value { font-size: 1.75rem; font-weight: 750; margin-top: 6px; color: var(--m-text-primary); }
.m-kpi__value.success { color: #0B6E63; }
.m-kpi__value.warning { color: #9A5B12; }
.m-kpi__delta { font-size: 0.78rem; margin-top: 3px; color: var(--m-text-tertiary); }
.m-kpi__delta.up { color: var(--m-status-success, #0D9488); }
.m-kpi__delta.warning { color: #9A5B12; font-weight: 600; }
.m-kpi__delta.teal { color: var(--m-teal-text, #0F766E); }

/* Tabs */
.rp-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--m-border); margin: 22px 0 0; flex-wrap: wrap; }
.rp-tab { padding: 9px 14px; font-size: 0.86rem; font-weight: 500; color: var(--m-text-secondary); border: none; background: none; border-bottom: 2px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.rp-tab .tdot { width: 7px; height: 7px; border-radius: 50%; flex: none; }
.rp-tab .tdot.success { background: var(--m-status-success, #0D9488); }
.rp-tab .tdot.warning { background: var(--m-status-warning, #D97706); }
.rp-tab .tdot.neutral { background: #5B6B85; }
.rp-tab .tdot.quiet { background: #AEB6C4; }
.rp-tab .tdot.all { background: var(--m-accent); }
.rp-tab.active { color: var(--m-text-primary); border-bottom-color: var(--m-accent); font-weight: 600; }
.rp-tab.active.moneytab { border-bottom-color: var(--m-status-success, #0D9488); }
.rp-tab .count { font-size: 0.7rem; background: #EEF1F6; color: var(--m-text-secondary); padding: 1px 7px; border-radius: 999px; font-weight: 700; }
.rp-tab.active .count { background: var(--m-accent-soft); color: var(--m-accent-hover); }
.rp-tab.active.moneytab .count { background: #E1F3EF; color: #0B6E63; }

.rp-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 16px 0; }
.rp-select, .rp-input { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px; font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit; }

/* Buttons */
.btn { font-family: inherit; font-weight: 500; font-size: 0.82rem; padding: 0.4rem 0.85rem; border-radius: 6px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-sm { font-size: 0.76rem; padding: 0.3rem 0.6rem; }
.rp-btn-primary { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.rp-btn-primary:hover:not(:disabled) { background: var(--m-accent-hover); border-color: var(--m-accent-hover); }
.rp-btn-secondary { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.rp-btn-secondary:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.rp-btn-violet { background: var(--m-violet, #7C3AED); border-color: var(--m-violet, #7C3AED); color: #fff; }
.rp-btn-violet:hover:not(:disabled) { background: #6D28D9; }
.btn-ghost { background: none; border: none; color: var(--m-text-secondary); padding: 6px 8px; cursor: pointer; border-radius: 6px; }
.btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg); color: var(--m-text-primary); }
.rp-stamp { display: inline-flex; align-items: center; gap: 5px; font-size: 0.72rem; font-weight: 700; color: #6B7386; text-transform: uppercase; letter-spacing: 0.03em; }

.rp-loading { padding: 32px; text-align: center; color: var(--m-text-secondary); }

/* Table */
.rp-tbl { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); overflow: visible; }
.rp-tbl table { width: 100%; border-collapse: collapse; }
.rp-tbl thead th { background: rgba(79, 70, 229, 0.07); border-bottom: 1px solid var(--m-border); text-align: left; font-size: 0.66rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--m-text-secondary); padding: 11px 14px; white-space: nowrap; }
.rp-tbl tbody td { padding: 0.75rem 0.85rem; font-size: 0.85rem; border-bottom: 1px solid var(--m-border); vertical-align: top; }
.rp-tbl tbody tr:last-child td { border-bottom: none; }
.rp-tbl tbody tr.is-open { background: rgba(13, 148, 136, 0.035); }
.rp-tbl tbody tr.money-row td:first-child { box-shadow: inset 3px 0 0 var(--m-status-success, #0D9488); }
.rp-tbl tbody tr.review-row td:first-child { box-shadow: inset 3px 0 0 var(--m-status-warning, #D97706); }
.sub-name { font-weight: 600; color: var(--m-text-primary); }
.sub-email { font-size: 0.76rem; color: var(--m-text-secondary); display: flex; align-items: center; gap: 5px; margin-top: 2px; }
.sub-email i { font-size: 0.85em; color: var(--m-text-tertiary); }
.sub-co { font-size: 0.74rem; color: var(--m-text-tertiary); margin-top: 3px; display: flex; align-items: center; gap: 5px; }
.chev { background: none; border: none; color: var(--m-text-tertiary); cursor: pointer; padding: 2px; display: inline-flex; transition: transform 0.15s; margin-top: 1px; }
.chev.open { transform: rotate(90deg); color: var(--m-status-success, #0D9488); }

/* Classification badge ladder */
.cls-badge { display: inline-flex; align-items: center; gap: 6px; padding: 0.2rem 0.55rem; font-size: 0.71rem; font-weight: 700; border-radius: 5px; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em; border: 1px solid transparent; }
.cls-badge .spark { color: var(--m-teal, #14B8A6); flex: none; font-size: 0.9em; }
.cls-badge .dot { width: 6px; height: 6px; border-radius: 50%; flex: none; }
.cls-badge.interested { background: #DCF2ED; color: #0B6E63; border-color: #BBE7DF; }
.cls-badge.interested .dot { background: var(--m-status-success, #0D9488); }
.cls-badge.ooo, .cls-badge.wrong { background: #EEF2F8; color: #41526B; border-color: #DCE3EE; }
.cls-badge.ooo .dot, .cls-badge.wrong .dot { background: #5B6B85; }
.cls-badge.unsub, .cls-badge.other { background: #F1F3F7; color: #6B7386; border-color: #E3E7EF; }
.cls-badge.unsub .dot, .cls-badge.other .dot { background: #AEB6C4; }
.cls-badge.notnow { background: #FFF7EC; color: #9A5B12; border-color: #F2E3C6; }
.cls-badge.notnow .dot { background: var(--m-status-warning, #D97706); }
.cls-badge.outlined { background: #fff; }
.cls-badge.outlined.notnow { border-color: #E7B968; color: #9A5B12; }
.cls-badge.outlined.wrong, .cls-badge.outlined.other, .cls-badge.outlined.ooo, .cls-badge.outlined.unsub, .cls-badge.outlined.interested { border-color: #C7CFDC; color: #41526B; }

.conf { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.conf__bar { display: flex; align-items: center; gap: 7px; }
.conf__track { width: 66px; height: 5px; border-radius: 999px; background: #EDF0F5; overflow: hidden; flex: none; }
.conf__track span { display: block; height: 100%; border-radius: 999px; }
.conf__track span.success { background: var(--m-status-success, #0D9488); }
.conf__track span.warning { background: var(--m-status-warning, #D97706); }
.conf__num { font-size: 0.72rem; font-weight: 700; color: var(--m-text-secondary); }
.conf__state { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; display: inline-flex; align-items: center; gap: 4px; }
.conf__state.auto { color: #0B6E63; }
.conf__state.needs { color: #9A5B12; }

/* what-we-did */
.did { display: flex; align-items: flex-start; gap: 7px; font-size: 0.82rem; line-height: 1.4; color: var(--m-text-primary); max-width: 290px; }
.did i { flex: none; margin-top: 2px; }
.did.auto i { color: var(--m-status-success, #0D9488); }
.did.delay i { color: var(--m-status-info, #0284C7); }
.did.stop i { color: #6B7386; }
.did.hold i { color: var(--m-status-warning, #D97706); }
.did .em { font-weight: 600; }

/* source */
.src { font-size: 0.8rem; color: var(--m-text-primary); font-weight: 500; display: flex; align-items: center; gap: 6px; }
.src i { color: var(--m-text-tertiary); font-size: 0.9em; }
.src__step { font-size: 0.72rem; color: var(--m-text-secondary); margin-top: 3px; display: flex; align-items: center; gap: 5px; max-width: 240px; }
.src__step .pill { background: #EEF1F6; border-radius: 4px; padding: 0.5px 5px; font-weight: 600; color: var(--m-text-secondary); flex: none; }

.age { font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; color: var(--m-text-secondary); white-space: nowrap; }
.age i { color: var(--m-text-tertiary); }
.age.fresh { color: #0B6E63; font-weight: 600; }
.age.fresh i { color: var(--m-status-success, #0D9488); }

.row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; flex-wrap: wrap; position: relative; }
.rp-menu { position: absolute; top: 100%; right: 0; margin-top: 4px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 8px; box-shadow: var(--m-shadow-panel); z-index: 10; min-width: 160px; padding: 4px; }
.rp-menu button { display: flex; align-items: center; gap: 8px; width: 100%; background: none; border: none; text-align: left; padding: 8px 10px; font-size: 0.82rem; color: var(--m-text-primary); cursor: pointer; border-radius: 6px; }
.rp-menu button:hover { background: var(--m-rail-hover-bg); }

/* Drill-down */
.drill { background: #FBFCFE; }
.drill__inner { display: grid; grid-template-columns: 1.12fr 1fr; gap: 26px; padding: 22px 26px; }
@media (max-width: 900px) { .drill__inner { grid-template-columns: 1fr; } }
.drill h4 { margin: 0 0 12px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--m-text-secondary); display: flex; align-items: center; gap: 7px; }
.drill h4 i { color: var(--m-text-tertiary); }
.drill h4 + .facts, .drill h4 + .timeline2 { margin-top: 0; }

.ai-read { background: linear-gradient(135deg, var(--m-teal-soft, #E4F7F4), #F4FCFB); border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 12px; padding: 15px 16px; margin-bottom: 18px; }
.ai-read__head { display: flex; align-items: center; gap: 8px; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-teal-text, #0F766E); }
.ai-read__head i { color: var(--m-teal, #14B8A6); }
.ai-read__badge { margin-left: auto; font-size: 0.58rem; font-weight: 800; letter-spacing: 0.04em; color: var(--m-teal-text, #0F766E); background: var(--m-bg-card); border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 999px; padding: 2px 8px; }
.ai-read__body { font-size: 0.88rem; line-height: 1.55; margin-top: 10px; color: var(--m-text-primary); }
.ai-read__body b { color: #0A5A50; }
.ai-read__note { display: flex; gap: 7px; align-items: flex-start; margin-top: 11px; font-size: 0.74rem; color: var(--m-text-secondary); line-height: 1.45; }
.ai-read__note i { flex: none; margin-top: 1px; color: var(--m-teal-text, #0F766E); }

.facts { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 18px; }
.fact-chip { display: inline-flex; flex-direction: column; gap: 1px; font-size: 0.82rem; font-weight: 600; padding: 0.4rem 0.7rem; border-radius: 8px; background: #F4F6FA; border: 1px solid var(--m-border); color: var(--m-text-primary); }
.fact-chip .k { font-size: 0.58rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 700; }
.fact-chip.date { background: #EAF4FB; border-color: #CFE6F5; color: #0A5A7A; }
.fact-chip.date .k { color: #3E7FA1; }

.timeline2 { position: relative; padding-left: 20px; margin-bottom: 6px; }
.timeline2::before { content: ""; position: absolute; left: 5px; top: 5px; bottom: 5px; width: 2px; background: var(--m-border-strong); }
.tl2 { position: relative; padding: 0 0 14px; }
.tl2::before { content: ""; position: absolute; left: -18px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--m-bg-card); border: 2px solid var(--m-text-tertiary); }
.tl2.auto::before { background: var(--m-status-success, #0D9488); border-color: var(--m-status-success, #0D9488); }
.tl2.ai::before { background: var(--m-teal, #14B8A6); border-color: var(--m-teal, #14B8A6); }
.tl2__t { font-weight: 600; font-size: 0.84rem; }
.tl2__t .mini { font-weight: 700; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.03em; color: #0B6E63; background: #E1F3EF; border: 1px solid #BBE7DF; border-radius: 4px; padding: 0 5px; margin-left: 6px; }
.tl2__t .mini.ai { color: var(--m-teal-text, #0F766E); background: var(--m-teal-soft, #E4F7F4); border-color: var(--m-teal-line, #B6EBE4); }
.tl2__t .mini.log { color: #6B7386; background: #F1F3F7; border-color: #E3E7EF; }
.tl2__m { font-size: 0.74rem; color: var(--m-text-secondary); margin-top: 1px; }

.prospect { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; }
.prospect__top { display: flex; align-items: center; gap: 10px; }
.prospect__name { font-weight: 600; font-size: 0.9rem; }
.prospect__link { margin-left: auto; font-size: 0.78rem; font-weight: 600; color: var(--m-accent-hover); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.prospect__link:hover { text-decoration: underline; }
.prospect__meta { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 11px; }
.stage-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 600; padding: 0.16rem 0.55rem; border-radius: 999px; background: var(--m-accent-soft); color: var(--m-accent-hover); }
.temp { display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; font-weight: 700; padding: 0.16rem 0.55rem; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.03em; }
.temp .dot { width: 6px; height: 6px; border-radius: 50%; }
.temp.warm { background: #FFF4E6; color: #9A5B12; }
.temp.warm .dot { background: var(--m-status-warning, #D97706); }
.temp.hot { background: #E1F3EF; color: #0B6E63; }
.temp.hot .dot { background: var(--m-status-success, #0D9488); }
.temp.cold { background: #EEF2F8; color: #41526B; }
.temp.cold .dot { background: #5B6B85; }

/* wrong-person suggested contact */
.suggest { border: 1px solid var(--m-border); border-radius: 12px; overflow: hidden; background: var(--m-bg-card); margin-bottom: 16px; }
.suggest__head { display: flex; align-items: center; gap: 8px; padding: 11px 14px; background: #F8FAFC; border-bottom: 1px solid var(--m-border); font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-secondary); }
.suggest__head i { color: var(--m-text-tertiary); }
.suggest__body { padding: 14px 16px; }
.contact-card { display: flex; align-items: flex-start; gap: 11px; padding: 12px 13px; border: 1px solid var(--m-border); border-radius: 10px; background: #FBFCFE; }
.contact-card__av { width: 36px; height: 36px; border-radius: 50%; background: var(--m-brand-gradient, linear-gradient(135deg, #4F46E5, #0EA5A4)); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 0.72rem; flex: none; }
.contact-card__name { font-weight: 600; font-size: 0.88rem; }
.contact-card__email { font-size: 0.77rem; color: var(--m-text-secondary); margin-top: 1px; }
.prov { display: inline-flex; align-items: center; gap: 5px; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.14rem 0.5rem; border-radius: 999px; margin-top: 7px; }
.prov.referral { background: #FFF7EC; color: #9A5B12; border: 1px solid #F2E3C6; }
.prov.verified { background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); border: 1px solid var(--m-teal-line, #B6EBE4); }
.suggest__note { display: flex; gap: 7px; align-items: flex-start; margin-top: 12px; font-size: 0.75rem; color: var(--m-text-secondary); line-height: 1.45; }
.suggest__note i { flex: none; margin-top: 1px; color: var(--m-violet, #7C3AED); }
.suggest__cta { display: flex; gap: 9px; align-items: center; flex-wrap: wrap; margin-top: 13px; }

/* reclassify popover */
.reclass-pop { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 12px; box-shadow: var(--m-shadow-panel); overflow: hidden; margin-bottom: 16px; }
.reclass-pop__head { padding: 12px 15px; border-bottom: 1px solid var(--m-border); }
.reclass-pop__head .t { font-weight: 600; font-size: 0.88rem; }
.reclass-pop__head .s { font-size: 0.74rem; color: var(--m-text-secondary); margin-top: 2px; }
.reclass-list { padding: 6px; }
.reclass-opt { display: flex; align-items: center; gap: 10px; padding: 9px 11px; border-radius: 8px; cursor: pointer; font-size: 0.85rem; }
.reclass-opt:hover { background: var(--m-rail-hover-bg); }
.reclass-opt.current { background: #DCF2ED; cursor: default; }
.reclass-opt .odot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.reclass-opt .oname { font-weight: 500; }
.reclass-opt .ocur { margin-left: auto; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #0B6E63; background: var(--m-bg-card); border: 1px solid #BBE7DF; border-radius: 999px; padding: 1px 7px; }
.reclass-opt .ocheck { margin-left: auto; color: var(--m-accent); opacity: 0; }
.reclass-opt:hover .ocheck { opacity: 0.6; }
.reclass-pop__foot { padding: 11px 15px; border-top: 1px solid var(--m-border); background: #FBFCFE; display: flex; flex-direction: column; gap: 10px; }
.reclass-warn { display: flex; gap: 7px; align-items: flex-start; font-size: 0.73rem; color: var(--m-text-secondary); line-height: 1.45; }
.reclass-warn i { flex: none; margin-top: 1px; color: var(--m-status-warning, #D97706); }
.reclass-pop__actions { display: flex; gap: 8px; justify-content: flex-end; }

.drill__cta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--m-border); }

/* Empty state */
.dc-empty { text-align: center; padding: 48px 24px; color: var(--m-text-secondary); background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); margin-top: 16px; }
.dc-empty.teal-edge { border-top: 3px solid var(--m-teal, #14B8A6); }
.dc-empty__icon { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); font-size: 24px; margin-bottom: 16px; }
.dc-empty__title { font-weight: 600; color: var(--m-text-primary); font-size: 1rem; margin-bottom: 8px; }
.dc-empty__hint { font-size: 0.86rem; max-width: 560px; margin: 0 auto 8px; line-height: 1.6; }
.dc-empty__why { max-width: 560px; margin: 14px auto 0; text-align: left; display: grid; gap: 8px; }
.dc-empty__why .row { display: flex; gap: 9px; align-items: flex-start; font-size: 0.82rem; line-height: 1.45; }
.dc-empty__why .row i { color: var(--m-status-info); margin-top: 3px; }
.dc-empty__actions { margin-top: 22px; display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
.foot-note { font-size: 0.75rem; color: var(--m-text-tertiary); margin-top: 8px; line-height: 1.5; }

/* Violet Growth teaser */
.rp-teaser { position: relative; border-radius: var(--m-radius-lg, 14px); overflow: hidden; border: 1px solid var(--m-border); margin: 20px 0; min-height: 340px; }
.rp-teaser__blur { filter: blur(4px); opacity: 0.55; pointer-events: none; user-select: none; padding: 8px; }
.rp-skel-tbl { background: var(--m-bg-card); }
.rp-skel-row { display: flex; gap: 18px; align-items: center; padding: 16px 18px; border-bottom: 1px solid var(--m-border); }
.skel { height: 11px; border-radius: 5px; background: linear-gradient(90deg, #E9ECF3, #F3F5F9, #E9ECF3); display: inline-block; }
.rp-teaser__panel { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, rgba(243, 238, 254, 0.72), rgba(251, 250, 255, 0.82)); }
.upgrade { max-width: 560px; text-align: center; background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF); border: 1px solid var(--m-violet-line, #E0D2FB); border-radius: 16px; padding: 30px 34px; box-shadow: var(--m-shadow-panel); }
.upgrade__ic { width: 52px; height: 52px; border-radius: 14px; background: var(--m-bg-card); border: 1px solid var(--m-violet-line, #E0D2FB); color: var(--m-violet, #7C3AED); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 14px; }
.upgrade__eyebrow { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--m-violet, #7C3AED); margin-bottom: 8px; }
.upgrade h3 { margin: 0 0 8px; font-size: 1.25rem; }
.upgrade p { margin: 0 0 18px; font-size: 0.88rem; color: var(--m-text-secondary); line-height: 1.55; }
.upgrade__benefits { text-align: left; display: grid; gap: 10px; margin: 0 auto 20px; max-width: 430px; }
.benefit { display: flex; gap: 10px; align-items: flex-start; font-size: 0.86rem; line-height: 1.4; }
.benefit i { color: var(--m-violet, #7C3AED); margin-top: 3px; }
.upgrade__honest { max-width: 430px; margin: 0 auto 20px; padding: 11px 14px; background: var(--m-bg-card); border: 1px solid var(--m-violet-line, #E0D2FB); border-radius: 10px; font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; text-align: left; display: flex; gap: 8px; }
.upgrade__honest i { color: var(--m-violet, #7C3AED); flex: none; margin-top: 1px; }
.upgrade__actions { display: flex; gap: 10px; justify-content: center; align-items: center; }
.rp-compare { font-size: 0.85rem; font-weight: 700; color: var(--m-violet, #7C3AED); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
.rp-compare:hover { text-decoration: underline; }
</style>
